"use client";

import Link from "next/link";
import { FormEvent, KeyboardEvent, PointerEvent, useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { clampAssistantPosition, defaultAssistantPosition, type AssistantPosition } from "../../lib/ui/assistantPosition";
import { shouldSubmitAssistantComposer } from "../../lib/ui/assistantKeyboard";
import { vinteraBusinessIdentity, vinteraPostalAddress } from "../../lib/businessIdentity";

type AssistantItem = { label?: string; value?: string; detail?: string; href?: string };
type AssistantPayload = { answer?: string; error?: string; result?: { items?: AssistantItem[] } | null; references?: unknown[] };
type Message = { question: string; answer: string; items: AssistantItem[] };

const POSITION_KEY = "vintera.assistant.position.v1";
const EDGE_GAP = 16;
const publicLinks = [
  { label: "Apply for an account", href: "/apply" },
  { label: "Customer login", href: "/login" },
  { label: "Contact Vintera", href: "/contact" },
];

function launcherSize() {
  return window.matchMedia("(min-width: 640px)").matches ? 80 : 64;
}

function defaultPosition(): AssistantPosition {
  const size = launcherSize();
  return defaultAssistantPosition(window.innerWidth, window.innerHeight, size, EDGE_GAP);
}

function clampPosition(position: AssistantPosition): AssistantPosition {
  return clampAssistantPosition(position, window.innerWidth, window.innerHeight, launcherSize(), EDGE_GAP);
}

function publicAnswer(question: string): Message {
  const normalized = question.toLowerCase();
  if (/admin|staff/.test(normalized)) return { question, answer: "Individual Vintera staff can use the secure staff sign-in page. I cannot access or change accounts.", items: [{ label: "Staff login", href: "/admin/login" }] };
  if (/contact|phone|email|fax|address|located|location/.test(normalized)) return {
    question,
    answer: `${vinteraBusinessIdentity.legalName} is located at ${vinteraPostalAddress()}. Email ${vinteraBusinessIdentity.supportEmail}, call ${vinteraBusinessIdentity.phoneDisplay}, or fax ${vinteraBusinessIdentity.faxDisplay}.`,
    items: [{ label: "Contact Vintera", href: "/contact" }],
  };
  if (/apply|account|customer/.test(normalized)) return { question, answer: "Wholesale healthcare businesses can begin with Vintera's account application. Existing customers can use Customer Login.", items: publicLinks.slice(0, 2) };
  return { question, answer: "I can point you to public Vintera pages. Sign in for read-only help based on records your account is authorized to view.", items: publicLinks };
}

export default function VinteraAssistantPet() {
  const pathname = usePathname();
  const role = pathname.startsWith("/portal") ? "customer" : pathname.startsWith("/admin") && !/\/(login|forgot-password|reset-password|mfa)/.test(pathname) ? "staff" : "public";
  // The root layout persists across sign-out navigation. Do not retain another
  // role's transcript, references, or in-flight conversation in the next session.
  return <AssistantPetContent key={role} pathname={pathname} role={role} />;
}

function AssistantPetContent({ pathname, role }: { pathname: string; role: "customer" | "staff" | "public" }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [position, setPosition] = useState<AssistantPosition | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startY: number; origin: AssistantPosition; moved: boolean } | null>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const panelId = useId();
  const conversationId = useRef<string | null>(null);
  const references = useRef<unknown[]>([]);

  useEffect(() => {
    let next = defaultPosition();
    try {
      const stored = window.localStorage.getItem(POSITION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<AssistantPosition>;
        if (Number.isFinite(parsed.x) && Number.isFinite(parsed.y)) next = { x: Number(parsed.x), y: Number(parsed.y) };
      }
    } catch {
      // A blocked/corrupt preference must not prevent access to the assistant.
    }
    setPosition(window.innerWidth < 640 ? next : clampPosition(next));
    const reconcile = () => setPosition((current) => current ? window.innerWidth < 640 ? { ...current } : clampPosition(current) : defaultPosition());
    window.addEventListener("resize", reconcile);
    return () => window.removeEventListener("resize", reconcile);
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("assistant") === "open") setOpen(true);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const launcher = launcherRef.current;
    composerRef.current?.focus();
    return () => { if (launcher?.isConnected) launcher.focus(); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      const conversation = conversationRef.current;
      if (conversation) conversation.scrollTop = conversation.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, messages, busy]);

  function persist(next: AssistantPosition) {
    setPosition(next);
    try { window.localStorage.setItem(POSITION_KEY, JSON.stringify(next)); } catch { /* Visual persistence is optional. */ }
  }

  function pointerDown(event: PointerEvent<HTMLButtonElement>) {
    if (!position || event.button !== 0 || window.innerWidth < 640) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, origin: position, moved: false };
    setDragging(true);
  }

  function pointerMove(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (Math.hypot(dx, dy) > 5) current.moved = true;
    setPosition(clampPosition({ x: current.origin.x + dx, y: current.origin.y + dy }));
  }

  function pointerEnd(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const wasMoved = current.moved;
    drag.current = null;
    setDragging(false);
    if (position) persist(clampPosition(position));
    if (!wasMoved) setOpen((value) => !value);
  }

  function pointerCancel(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    setPosition(current.origin);
  }

  function keyboardToggle(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((value) => !value);
    }
  }

  async function ask(event: FormEvent) {
    event.preventDefault();
    const value = question.trim();
    if (!value || busy) return;
    setQuestion("");
    if (role === "public") { setMessages((current) => [...current.slice(-4), publicAnswer(value)]); return; }
    setBusy(true);
    try {
      conversationId.current ||= crypto.randomUUID();
      const response = await fetch(role === "customer" ? "/api/portal/assistant" : "/api/admin/assistant", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: conversationId.current, requestKey: crypto.randomUUID(), question: value, priorReferences: references.current }),
      });
      const payload = await response.json() as AssistantPayload;
      if (!response.ok) throw new Error(payload.error || "Ask Vintera is temporarily unavailable.");
      references.current = Array.isArray(payload.references) ? payload.references : [];
      setMessages((current) => [...current.slice(-4), { question: value, answer: payload.answer || "No answer is available.", items: payload.result?.items?.slice(0, 4) || [] }]);
    } catch (error) {
      setMessages((current) => [...current.slice(-4), { question: value, answer: error instanceof Error ? error.message : "Ask Vintera is temporarily unavailable.", items: [] }]);
    } finally { setBusy(false); }
  }

  if (!position) return null;
  const mobile = typeof window !== "undefined" && window.innerWidth < 640;
  const expandLeft = mobile || position.x > (typeof window === "undefined" ? 500 : window.innerWidth / 2);
  const expandUp = mobile || position.y > (typeof window === "undefined" ? 400 : window.innerHeight / 2);
  const availablePanelHeight = typeof window === "undefined"
    ? 576
    : expandUp
      ? position.y - EDGE_GAP - 12
      : window.innerHeight - position.y - launcherSize() - EDGE_GAP - 12;
  const panelHeight = Math.max(220, Math.min(576, availablePanelHeight));

  function submitOnEnter(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (shouldSubmitAssistantComposer({
      isComposing: Boolean((event.nativeEvent as unknown as { isComposing?: boolean }).isComposing),
      key: event.key,
      shiftKey: event.shiftKey,
    })) {
      event.preventDefault();
      if (!busy && question.trim()) event.currentTarget.form?.requestSubmit();
    }
  }

  return <div onKeyDown={(event) => { if (open && event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setOpen(false); } }} className="fixed z-40 h-16 w-16 sm:h-20 sm:w-20" style={mobile ? { right: "max(16px, env(safe-area-inset-right))", bottom: "max(5rem, calc(env(safe-area-inset-bottom) + 1rem))" } : { left: position.x, top: position.y }}>
    {open && <section id={panelId} role="dialog" aria-modal="false" aria-label="Ask Vintera" style={{ height: mobile ? "min(36rem, calc(100dvh - 10rem - env(safe-area-inset-bottom)))" : panelHeight }} className={`absolute flex w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-[var(--vintera-border)] bg-white shadow-2xl ${expandLeft ? "right-0" : "left-0"} ${expandUp ? "bottom-[calc(100%+0.75rem)]" : "top-[calc(100%+0.75rem)]"}`}>
      <header className="flex shrink-0 items-center justify-between bg-[var(--vintera-navy)] px-4 py-3 text-white"><div><p className="font-bold">Ask Vintera</p><p className="text-xs text-blue-100">Read-only {role === "staff" ? "staff" : role === "customer" ? "customer" : "public"} help</p></div><button type="button" aria-label="Close Ask Vintera" onClick={() => setOpen(false)} className="min-h-11 min-w-11 rounded-full text-xl hover:bg-white/10">×</button></header>
      <div ref={conversationRef} data-assistant-conversation className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4"><p className="rounded-lg bg-[var(--vintera-sage-soft)] p-3 text-sm">I can explain and link to authorized Vintera information. I cannot place orders, change records, take payment, or modify security.</p>{messages.map((message, index) => <article key={`${message.question}-${index}`} className="mt-3 border-t border-slate-100 pt-3 text-sm"><p className="font-semibold text-[var(--vintera-sage-dark)]">You: {message.question}</p><p className="mt-2 whitespace-pre-wrap leading-6">{message.answer}</p>{message.items.map((item, itemIndex) => item.href?.startsWith("/") ? <Link key={`${item.href}-${itemIndex}`} href={item.href} onClick={() => setOpen(false)} className="mt-2 block rounded-md border border-[var(--vintera-border)] p-2 font-semibold text-[var(--vintera-navy)] hover:bg-[var(--vintera-sage-soft)]">{item.label || item.value || "Open in Vintera"}{item.detail ? <span className="mt-1 block font-normal text-[var(--vintera-muted)]">{item.detail}</span> : null}</Link> : null)}</article>)}</div>
      <form onSubmit={ask} data-assistant-composer className="shrink-0 border-t border-[var(--vintera-border)] bg-white p-3"><label htmlFor="vintera-pet-question" className="sr-only">Question</label><textarea ref={composerRef} id="vintera-pet-question" value={question} onChange={(event) => setQuestion(event.target.value)} onKeyDown={submitOnEnter} maxLength={500} rows={2} placeholder="How can Vintera help?" className="w-full resize-none rounded-md border border-[var(--vintera-border)] px-3 py-2 text-base" /><p className="mt-1 text-xs text-[var(--vintera-muted)]">Enter sends · Shift+Enter starts a new line</p><button disabled={busy || !question.trim()} className="mt-2 w-full rounded-md bg-[var(--vintera-navy)] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Checking…" : "Ask"}</button></form>
    </section>}
    <button ref={launcherRef} type="button" aria-controls={open ? panelId : undefined} aria-expanded={open} aria-label={mobile ? open ? "Close Ask Vintera" : "Open Ask Vintera" : open ? "Move or close Ask Vintera" : "Move or open Ask Vintera"} onClick={(event) => { if (mobile && event.detail > 0) setOpen((value) => !value); }} onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerEnd} onPointerCancel={pointerCancel} onKeyDown={keyboardToggle} className={`group flex h-16 w-16 touch-none select-none items-center justify-center rounded-full border-4 border-white bg-[var(--vintera-navy)] shadow-xl transition hover:scale-105 sm:h-20 sm:w-20 ${dragging ? "cursor-grabbing" : mobile ? "cursor-pointer" : "cursor-grab"}`}>
      <span className="relative block h-10 w-6 rotate-45 overflow-hidden rounded-full border-2 border-white bg-white sm:h-12 sm:w-7"><span className="absolute inset-x-0 top-0 h-1/2 bg-[var(--vintera-sage)]" /><span className="absolute inset-x-0 top-1/2 h-0.5 bg-[var(--vintera-navy)]" /></span><span className="sr-only">Ask Vintera</span>
    </button>
  </div>;
}
